import { FC } from 'react'
import { useSelector } from 'react-redux'
import { cloneDeep, orderBy, find } from 'lodash'
import { addDays, isBefore, isSameMinute, parseISO } from 'date-fns'

import { Format } from 'services/Dates'
import AvailabilityRangeSummary from 'components/Requests/AvailabilityRangeSummary'

import { t } from 'translations'

const AvailabilitiesDispo: FC<{ days: MData.Day[] }> = ({ days }) => {
  //Copy the array of days to avoid problems
  const newDays = cloneDeep(days)

  const { defaultBeginningOfWeekDay } = useSelector((state: MStore.RootState) => state.settings)

  let dayOrder: MData.Day['dayOfWeek'][] = [0, 1, 2, 3, 4, 5, 6]
  if (defaultBeginningOfWeekDay ?? 0 !== 0) dayOrder = [...dayOrder.slice(defaultBeginningOfWeekDay), ...dayOrder.slice(0, defaultBeginningOfWeekDay)]

  // Function to concat availabilities that are overlapping
  const concatAvailabilities = (
    type: MData.Day['availabilities'][number]['type'],
    availabilities: MData.Day['availabilities'],
    newAvailabilities: MData.Day['availabilities'] = []
  ) => {
    const date = Format(new Date(), { outFormat: 'date' })
    // Order the availabilities by start date ascending
    const orderedAvailabilities = orderBy(availabilities, ['start'], ['asc'])
    // Only concat the availabilities if there are more than 1 block
    if (orderedAvailabilities.length < 1) return orderedAvailabilities

    const start = orderedAvailabilities[0].start
    let end = orderedAvailabilities[0].end
    let endMoment = parseISO(date + 'T' + end + ':00')
    if (start > end) endMoment = addDays(endMoment, 1)

    for (let i = 1; i < orderedAvailabilities.length; i++) {
      const availabilityStartMoment = parseISO(date + 'T' + orderedAvailabilities[i].start + ':00')
      let availabilityEndMoment = parseISO(date + 'T' + orderedAvailabilities[i].end + ':00')
      if (orderedAvailabilities[i].start > orderedAvailabilities[i].end) availabilityEndMoment = addDays(availabilityEndMoment, 1)

      if (isBefore(availabilityStartMoment, endMoment) || isSameMinute(availabilityStartMoment, endMoment)) {
        if (isBefore(availabilityEndMoment, endMoment) || isSameMinute(availabilityEndMoment, endMoment)) continue
        end = orderedAvailabilities[i].end
        endMoment = parseISO(date + 'T' + end + ':00')
        if (orderedAvailabilities[i].start > orderedAvailabilities[i].end) endMoment = addDays(endMoment, 1)
      } else {
        concatAvailabilities(type, orderedAvailabilities.slice(i, orderedAvailabilities.length + 1), newAvailabilities)
        break
      }
    }
    newAvailabilities.push({ start, end, type })
    return orderBy(newAvailabilities, ['start'], ['asc'])
  }

  // Concat the availabilities by type if needed
  for (const day of newDays) {
    const { availabilities } = day
    const unavailable = availabilities.filter(availability => availability.type === 'UNAVAILABLE')
    const available = availabilities.filter(availability => availability.type === 'AVAILABLE')
    const preferred = availabilities.filter(availability => availability.type === 'PREFERRED')

    day.availabilities = [
      ...concatAvailabilities('UNAVAILABLE', unavailable),
      ...concatAvailabilities('AVAILABLE', available),
      ...concatAvailabilities('PREFERRED', preferred)
    ]
  }

  return (
    <>
      {dayOrder.map(dayOfWeek => {
        const day = find(newDays, { dayOfWeek })
        if (!day) return null
        return (
          <div key={dayOfWeek}>
            <b>{t(`components.dayOfWeek.${day.dayOfWeek}`)}</b>
            {/* Wanted new availablities */}
            {!day.notAvailable && day.availabilities.length === 0 ? (
              <span className="text-success"> {t('components.availabilities.AVAILABLE_ALL_DAY')}</span>
            ) : null}
            {day.notAvailable && day.availabilities.length === 0 ? (
              <span className="text-danger"> {t('components.availabilities.NOT_AVAILABLE_ALL_DAY')}</span>
            ) : null}
            {day.availabilities.map((range, index) => (
              <span key={index}>
                <AvailabilityRangeSummary range={range} />
                {day.availabilities[index + 1] ? <span>, </span> : null}
              </span>
            ))}
          </div>
        )
      })}
    </>
  )
}

export default AvailabilitiesDispo
